'use es6';

import { useTimeout } from "../react-use-timeout";
import { useUpdateEffect } from "../react-use-update-effect";
import { runIfFn } from "../shared-utils";
import { useIsPresent, motion, AnimatePresence } from "../../framer-motion";
import { chakra, useChakra, ThemeProvider, ColorModeContext } from "../system";
import { memo, useState, useEffect, useMemo, useSyncExternalStore } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Alert, AlertIcon, AlertTitle, AlertDescription } from "../alert";
import { CloseButton } from "../close-button";
import { Portal } from "../portal";
import { createContext } from "../react-context";
import defaultTheme from "../theme"; // src/toast.utils.ts

var findById = (arr, id) => arr.find(toast => toast.id === id);

function findToast(toasts, id) {
  const position = getToastPosition(toasts, id);
  const index = position ? toasts[position].findIndex(toast => toast.id === id) : -1;
  return {
    position,
    index
  };
}

function getToastPosition(toasts, id) {
  for (const [position, values] of Object.entries(toasts)) {
    if (findById(values, id)) {
      return position;
    }
  }
}

function getToastStyle(position) {
  const isRighty = position.includes("right");
  const isLefty = position.includes("left");
  let alignItems = "center";
  if (isRighty) alignItems = "flex-end";
  if (isLefty) alignItems = "flex-start";
  return {
    display: "flex",
    flexDirection: "column",
    alignItems
  };
}

function getToastListStyle(position) {
  const isTopOrBottom = position === "top" || position === "bottom";
  const margin = isTopOrBottom ? "0 auto" : void 0;
  const top = position.includes("top") ? "env(safe-area-inset-top, 0px)" : void 0;
  const bottom = position.includes("bottom") ? "env(safe-area-inset-bottom, 0px)" : void 0;
  const right = !position.includes("left") ? "env(safe-area-inset-right, 0px)" : void 0;
  const left = !position.includes("right") ? "env(safe-area-inset-left, 0px)" : void 0;
  return {
    position: "fixed",
    zIndex: "var(--toast-z-index, 5500)",
    pointerEvents: "none",
    display: "flex",
    flexDirection: "column",
    margin,
    top,
    bottom,
    right,
    left
  };
}

var toastMotionVariants = {
  initial: props => {
    const {
      position
    } = props;
    const dir = ["top", "bottom"].includes(position) ? "y" : "x";
    let factor = ["top-right", "bottom-right"].includes(position) ? 1 : -1;
    if (position === "bottom") factor = 1;
    return {
      opacity: 0,
      [dir]: factor * 24
    };
  },
  animate: {
    opacity: 1,
    y: 0,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.85,
    transition: {
      duration: 0.2,
      ease: [0.4, 0, 1, 1]
    }
  }
};
var ToastComponent = /*#__PURE__*/memo(props => {
  const {
    id,
    message,
    onCloseComplete,
    onRequestRemove,
    requestClose = false,
    position = "bottom",
    duration = 5e3,
    containerStyle,
    motionVariants = toastMotionVariants,
    toastSpacing = "0.5rem"
  } = props;
  const [delay, setDelay] = useState(duration);
  const isPresent = useIsPresent();
  useUpdateEffect(() => {
    if (!isPresent) {
      onCloseComplete == null ? void 0 : onCloseComplete();
    }
  }, [isPresent]);
  useUpdateEffect(() => {
    setDelay(duration);
  }, [duration]);

  const onMouseEnter = () => setDelay(null);

  const onMouseLeave = () => setDelay(duration);

  const close = () => {
    if (isPresent) onRequestRemove();
  };

  useEffect(() => {
    if (isPresent && requestClose) {
      onRequestRemove();
    }
  }, [isPresent, requestClose, onRequestRemove]);
  useTimeout(close, delay);
  const containerStyles = useMemo(() => Object.assign({
    pointerEvents: "auto",
    maxWidth: 560,
    minWidth: 300,
    margin: toastSpacing
  }, containerStyle), [containerStyle, toastSpacing]);
  const toastStyle = useMemo(() => getToastStyle(position), [position]);
  return /* @__PURE__ */jsx(motion.li, {
    layout: true,
    className: "chakra-toast",
    variants: motionVariants,
    initial: "initial",
    animate: "animate",
    exit: "exit",
    onHoverStart: onMouseEnter,
    onHoverEnd: onMouseLeave,
    custom: {
      position
    },
    style: toastStyle,
    children: /* @__PURE__ */jsx(chakra.div, {
      role: "status",
      "aria-atomic": "true",
      className: "chakra-toast__inner",
      __css: containerStyles,
      children: runIfFn(message, {
        id,
        onClose: close
      })
    })
  });
});
ToastComponent.displayName = "ToastComponent"; // src/toast.placement.ts

function getToastPlacement(position, dir) {
  var _a;

  const computedPosition = position != null ? position : "bottom";
  const logicals = {
    "top-start": {
      ltr: "top-left",
      rtl: "top-right"
    },
    "top-end": {
      ltr: "top-right",
      rtl: "top-left"
    },
    "bottom-start": {
      ltr: "bottom-left",
      rtl: "bottom-right"
    },
    "bottom-end": {
      ltr: "bottom-right",
      rtl: "bottom-left"
    }
  };
  const logical = logicals[computedPosition];
  return (_a = logical == null ? void 0 : logical[dir]) != null ? _a : computedPosition;
} // src/toast.store.ts


var initialState = {
  top: [],
  "top-left": [],
  "top-right": [],
  "bottom-left": [],
  bottom: [],
  "bottom-right": []
};
var toastStore = createStore(initialState);

function createStore(initialState2) {
  let state = initialState2;
  const listeners = /* @__PURE__ */new Set();

  const setState = setStateFn => {
    state = setStateFn(state);
    listeners.forEach(l => l());
  };

  return {
    getState: () => state,
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        setState(() => initialState2);
        listeners.delete(listener);
      };
    },
    removeToast: (id, position) => {
      setState(prevState => Object.assign({}, prevState, {
        [position]: prevState[position].filter(toast => toast.id != id)
      }));
    },
    notify: (message, options) => {
      const toast = createToast(message, options);
      const {
        position,
        id
      } = toast;
      setState(prevToasts => {
        var _a, _b;

        const isTop = position.includes("top");
        const toasts = isTop ? [toast, ...((_a = prevToasts[position]) != null ? _a : [])] : [...((_b = prevToasts[position]) != null ? _b : []), toast];
        return Object.assign({}, prevToasts, {
          [position]: toasts
        });
      });
      return id;
    },
    update: (id, options) => {
      if (!id) return;
      setState(prevState => {
        const nextState = Object.assign({}, prevState);
        const {
          position,
          index
        } = findToast(nextState, id);

        if (position && index !== -1) {
          nextState[position][index] = Object.assign({}, nextState[position][index], {}, options, {
            message: createRenderToast(options)
          });
        }

        return nextState;
      });
    },
    closeAll: ({
      positions
    } = {}) => {
      setState(prev => {
        const allPositions = ["bottom", "bottom-right", "bottom-left", "top", "top-left", "top-right"];
        const positionsToClose = positions != null ? positions : allPositions;
        return positionsToClose.reduce((acc, position) => {
          acc[position] = prev[position].map(toast => Object.assign({}, toast, {
            requestClose: true
          }));
          return acc;
        }, Object.assign({}, prev));
      });
    },
    close: id => {
      setState(prevState => {
        const position = getToastPosition(prevState, id);
        if (!position) return prevState;
        return Object.assign({}, prevState, {
          [position]: prevState[position].map(toast => {
            if (toast.id == id) {
              return Object.assign({}, toast, {
                requestClose: true
              });
            }

            return toast;
          })
        });
      });
    },
    isActive: id => Boolean(findToast(toastStore.getState(), id).position)
  };
}

var counter = 0;

function createToast(message, options = {}) {
  var _a, _b;

  counter += 1;
  const id = (_a = options.id) != null ? _a : counter;
  const position = (_b = options.position) != null ? _b : "bottom";
  return {
    id,
    message,
    position,
    duration: options.duration,
    onCloseComplete: options.onCloseComplete,
    onRequestRemove: () => toastStore.removeToast(String(id), position),
    status: options.status,
    requestClose: false,
    containerStyle: options.containerStyle
  };
}

var Toast = props => {
  const {
    status,
    variant = "solid",
    id,
    title,
    isClosable,
    onClose,
    description,
    colorScheme,
    icon
  } = props;
  const ids = id ? {
    root: `toast-${id}`,
    title: `toast-${id}-title`,
    description: `toast-${id}-description`
  } : void 0;
  return /* @__PURE__ */jsxs(Alert, {
    addRole: false,
    status,
    variant,
    id: ids == null ? void 0 : ids.root,
    alignItems: "start",
    borderRadius: "md",
    boxShadow: "lg",
    paddingEnd: 8,
    textAlign: "start",
    width: "auto",
    colorScheme,
    children: [/* @__PURE__ */jsx(AlertIcon, {
      children: icon
    }), /* @__PURE__ */jsxs(chakra.div, {
      flex: "1",
      maxWidth: "100%",
      children: [title && /* @__PURE__ */jsx(AlertTitle, {
        id: ids == null ? void 0 : ids.title,
        children: title
      }), description && /* @__PURE__ */jsx(AlertDescription, {
        id: ids == null ? void 0 : ids.description,
        display: "block",
        children: description
      })]
    }), isClosable && /* @__PURE__ */jsx(CloseButton, {
      size: "sm",
      onClick: onClose,
      position: "absolute",
      insetEnd: 1,
      top: 1
    })]
  });
};

function createRenderToast(options = {}) {
  const {
    render,
    toastComponent: ToastComponent = Toast
  } = options;

  const renderToast = props => {
    if (typeof render === "function") {
      return render(Object.assign({}, props, {}, options));
    }

    return /* @__PURE__ */jsx(ToastComponent, Object.assign({}, props, {}, options));
  };

  return renderToast;
}

function createToastFn(dir, defaultOptions) {
  const normalizeToastOptions = options => {
    var _a;

    return Object.assign({}, defaultOptions, {}, options, {
      position: getToastPlacement((_a = options == null ? void 0 : options.position) != null ? _a : defaultOptions == null ? void 0 : defaultOptions.position, dir)
    });
  };

  const toast = options => {
    const normalizedToastOptions = normalizeToastOptions(options);
    const Message = createRenderToast(normalizedToastOptions);
    return toastStore.notify(Message, normalizedToastOptions);
  };

  toast.update = (id, options) => {
    toastStore.update(id, normalizeToastOptions(options));
  };

  toast.promise = (promise, options) => {
    const id = toast(Object.assign({}, options.loading, {
      status: "loading",
      duration: null
    }));
    promise.then(data => toast.update(id, Object.assign({
      status: "success",
      duration: 5e3
    }, runIfFn(options.success, data)))).catch(error => toast.update(id, Object.assign({
      status: "error",
      duration: 5e3
    }, runIfFn(options.error, error))));
  };

  toast.closeAll = toastStore.closeAll;
  toast.close = toastStore.close;
  toast.isActive = toastStore.isActive;
  return toast;
}

var [ToastOptionProvider, useToastOptionContext] = createContext({
  name: `ToastOptionsContext`,
  strict: false
});

var ToastProvider = props => {
  const state = useSyncExternalStore(toastStore.subscribe, toastStore.getState, toastStore.getState);
  const {
    motionVariants,
    component: Component = ToastComponent,
    portalProps
  } = props;
  const stateKeys = Object.keys(state);
  const toastList = stateKeys.map(position => {
    const toasts = state[position];
    return /* @__PURE__ */jsx("ul", {
      role: "region",
      "aria-live": "polite",
      id: `chakra-toast-manager-${position}`,
      style: getToastListStyle(position),
      children: /* @__PURE__ */jsx(AnimatePresence, {
        initial: false,
        children: toasts.map(toast => /* @__PURE__ */jsx(Component, Object.assign({
          motionVariants
        }, toast), toast.id))
      })
    }, position);
  });
  return /* @__PURE__ */jsx(Portal, Object.assign({}, portalProps, {
    children: toastList
  }));
};

function useToast(options) {
  const {
    theme
  } = useChakra();
  const defaultOptions = useToastOptionContext();
  return useMemo(() => createToastFn(theme.direction, Object.assign({}, defaultOptions, {}, options)), [options, theme.direction, defaultOptions]);
}

var defaults = {
  duration: 5e3,
  variant: "solid"
};
var defaultStandaloneParam = {
  theme: defaultTheme,
  colorMode: "light",
  toggleColorMode: () => {},
  setColorMode: () => {},
  defaultOptions: defaults,
  forced: false
};

function createStandaloneToast({
  theme = defaultStandaloneParam.theme,
  colorMode = defaultStandaloneParam.colorMode,
  toggleColorMode = defaultStandaloneParam.toggleColorMode,
  setColorMode = defaultStandaloneParam.setColorMode,
  defaultOptions = defaultStandaloneParam.defaultOptions,
  motionVariants,
  toastSpacing,
  component,
  forced
} = defaultStandaloneParam) {
  const colorModeContextValue = {
    colorMode,
    setColorMode,
    toggleColorMode,
    forced
  };

  const ToastContainer = () => /* @__PURE__ */jsx(ThemeProvider, {
    theme,
    children: /* @__PURE__ */jsx(ColorModeContext.Provider, {
      value: colorModeContextValue,
      children: /* @__PURE__ */jsx(ToastProvider, {
        defaultOptions,
        motionVariants,
        toastSpacing,
        component
      })
    })
  });

  return {
    ToastContainer,
    toast: createToastFn(theme.direction, defaultOptions)
  };
}

export { Toast, ToastOptionProvider, ToastProvider, createRenderToast, createStandaloneToast, createToastFn, getToastPlacement, useToast };